import {
  campaignAnalyticsGranularitySchema,
  campaignAnalyticsKpiKeySchema,
  isPrivilegedRole,
  type PortfolioAnalyticsResponse,
  type PortfolioListItem,
} from "@adpilot/shared";
import { IconChevronRight, IconFolders, IconPlus } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  DataGridBody,
  DataGridHeader,
  dataGridRowClassName,
} from "@/components/patterns/DataTable";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { CreatePortfolioDialog } from "@/components/portfolio/CreatePortfolioDialog";
import { PortfolioPerformancePanel } from "@/components/portfolio/PortfolioPerformancePanel";
import { MetricHeaderCells, MetricValueCells } from "@/components/portfolio/portfolio-metrics";
import { BudgetHealthBadge } from "@/components/shared/BudgetHealthBadge";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useShopScope } from "@/hooks/use-shop-scope";
import { useFormatters } from "@/lib/format";
import { requireModule } from "@/lib/module-access";
import { fetchPortfolioAnalytics } from "@/lib/portfolio-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/portfolios")({
  beforeLoad: () => {
    requireModule("campaigns");
  },
  validateSearch: z.object({
    metric: campaignAnalyticsKpiKeySchema.catch("spend"),
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
  }),
  component: PortfoliosPage,
});

// name / state / members / budget-health / budget-type / budget / flight-dates,
// then the 12 shared metric columns.
const GRID =
  // pl-0 drops the grid's left padding so the sticky name column rests exactly
  // where `sticky left-0` pins it (no 14px drift on horizontal scroll); the
  // name cell re-adds its own pl-3.5 so text inset is unchanged.
  "grid-cols-[minmax(190px,1.6fr)_88px_70px_116px_110px_104px_180px_repeat(12,minmax(66px,1fr))] gap-x-4 pl-0";

function PortfoliosPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  // ADP-110: the analytics scope (multi-select, default all workspace shops)
  // drives the aggregated read; the sorted set keys the query.
  const { scopeShopIds } = useShopScope();
  const aggregate = scopeShopIds.length > 1;
  const user = useAuthStore((state) => state.user);
  const workspaceRole = user?.workspaces?.find(
    (workspace) => workspace.id === user?.activeWorkspaceId,
  )?.role;
  // Same write gate as campaign creation (ADP-66): member is view-only.
  const canCreate = isPrivilegedRole(workspaceRole);
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: [
      "portfolios",
      "analytics",
      scopeShopIds.join(","),
      range,
      timeZone,
      search.granularity,
    ],
    queryFn: () => fetchPortfolioAnalytics(range, search.granularity, timeZone, scopeShopIds),
    placeholderData: keepPreviousData,
  });

  const panel = (
    <PortfolioPerformancePanel
      kpis={query.data?.kpis ?? []}
      trend={query.data?.trend ?? []}
      dataThrough={query.data?.dataThrough ?? null}
      metric={search.metric}
      granularity={search.granularity}
      onMetricChange={(metric) =>
        void navigate({ search: (previous) => ({ ...previous, metric }), replace: true })
      }
      onGranularityChange={(granularity) =>
        void navigate({ search: (previous) => ({ ...previous, granularity }), replace: true })
      }
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      onRetry={() => void query.refetch()}
    />
  );

  return (
    <PortfoliosFrame>
      <PortfoliosHeading />
      {panel}
      <PortfoliosTable
        data={query.data}
        aggregate={aggregate}
        isLoading={query.isLoading}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onGoToCampaigns={() => void navigate({ to: "/campaigns" })}
        canCreate={canCreate}
        onCreate={() => setCreateOpen(true)}
      />
      <CreatePortfolioDialog open={createOpen} onOpenChange={setCreateOpen} />
    </PortfoliosFrame>
  );
}

function PortfoliosFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full w-full min-w-0 flex-1 flex-col gap-[14px] px-6 pb-[22px] pt-[22px]">
      {children}
    </div>
  );
}

function PortfoliosHeading() {
  const { t } = useTranslation("portfolios");
  return (
    <div className="flex shrink-0 flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 flex-1 basis-[180px]">
        <h1 className="text-[24px] font-semibold leading-7 tracking-[-0.02em] text-ink">
          {t("title")}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">{t("subtitle")}</p>
      </div>
      <ReportingFilters />
    </div>
  );
}

function PortfoliosTable({
  data,
  aggregate = false,
  isLoading,
  isError,
  onRetry,
  onGoToCampaigns,
  canCreate = false,
  onCreate,
}: {
  data: PortfolioAnalyticsResponse | undefined;
  /** ADP-110: multi-shop scope — rows carry their shop label. */
  aggregate?: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onGoToCampaigns: () => void;
  canCreate?: boolean;
  onCreate?: () => void;
}) {
  const { t } = useTranslation("portfolios");

  if (isLoading && !data) {
    return (
      <div className="space-y-2 rounded-[14px] border border-line bg-surface p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return <ErrorState message={t("loadFailed")} onRetry={onRetry} />;
  }

  if (data.portfolios.length === 0) {
    // Mirrors the campaigns list: an empty page leads with the create action
    // when the user can write; members keep the go-to-campaigns hint.
    return (
      <EmptyState
        icon={<IconFolders className="size-16" />}
        title={t("empty.title")}
        description={t("empty.description")}
        actionLabel={canCreate && onCreate ? t("create.button") : t("empty.cta")}
        onAction={canCreate && onCreate ? onCreate : onGoToCampaigns}
      />
    );
  }

  return (
    <section className="flex min-h-[520px] flex-1 flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
      {/* Toolbar mirrors the campaigns list header: create action on the left. */}
      {canCreate ? (
        <div className="flex h-[50px] shrink-0 items-center justify-between gap-4 border-b border-line px-3">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onCreate}>
              <IconPlus data-icon="inline-start" />
              {t("create.button")}
            </Button>
          </div>
        </div>
      ) : null}
      <div className="flex-1 overflow-auto paper-scrollbar">
        <div className="min-w-[1980px]">
          <DataGridHeader className={cn(GRID, "sticky top-0 z-20 bg-subtle")}>
            <span className="sticky left-0 z-30 border-r border-line bg-subtle pl-3.5">
              {t("columns.name")}
            </span>
            <span>{t("columns.state")}</span>
            <span className="text-right">{t("columns.members")}</span>
            <span>{t("columns.budgetHealth")}</span>
            <span>{t("columns.budgetType")}</span>
            <span className="text-right">{t("columns.budget")}</span>
            <span>{t("columns.flightDates")}</span>
            <MetricHeaderCells />
          </DataGridHeader>
          <DataGridBody>
            {data.portfolios.map((portfolio) => (
              <PortfolioRow
                key={portfolio.id}
                portfolio={portfolio}
                showShop={aggregate}
                currencyCode={data.currency ?? "USD"}
              />
            ))}
          </DataGridBody>
        </div>
      </div>
    </section>
  );
}

/** "2026-01-01 – 2026-12-31"; one side when only one bound is set; em dash when neither. */
function formatFlightDates(
  startDate: string | null,
  endDate: string | null,
  formatDate: (input: string) => string,
): string {
  const start = startDate ? formatDate(startDate) : null;
  const end = endDate ? formatDate(endDate) : null;
  if (start && end) return `${start} – ${end}`;
  return start ?? end ?? "—";
}

function PortfolioRow({
  portfolio,
  showShop = false,
  currencyCode = "USD",
}: {
  portfolio: PortfolioListItem;
  /** ADP-110: label rows with their shop in the aggregated (multi-shop) view. */
  showShop?: boolean;
  /** The scope's reporting currency for the (possibly converted) metric sums. */
  currencyCode?: string;
}) {
  const { t } = useTranslation("portfolios");
  const { formatCurrency, formatDate } = useFormatters();
  return (
    <Link
      to="/portfolios/$id"
      params={{ id: portfolio.id }}
      className={dataGridRowClassName("standard", cn(GRID, "bg-surface hover:bg-subtle"))}
    >
      {/* Sticky column must stay opaque AND full-height (self-stretch) to cover
          horizontally-scrolled cells — the row is `items-center`, so without
          self-stretch this cell is only as tall as its text and a taller chip
          (h-5) behind it peeks out top & bottom. bg-inherit mirrors the row's
          own opaque background (surface at rest, subtle on hover) so it's never
          a white patch and never lets a chip bleed through. */}
      <span className="sticky left-0 z-10 flex min-w-0 items-center gap-1.5 self-stretch border-r border-line bg-inherit pl-3.5 pr-3">
        <span className="min-w-0">
          <span className="block truncate text-[11px] font-semibold text-ink hover:underline">
            {portfolio.name}
          </span>
          {/* ADP-110: disambiguate identical entities across shops */}
          {showShop && portfolio.shopName ? (
            <span className="block truncate text-[9px] leading-3 text-muted-foreground">
              {portfolio.shopName}
            </span>
          ) : null}
        </span>
        <IconChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      </span>
      <span className="flex items-center gap-1.5 capitalize text-muted-foreground">
        <span
          className={cn(
            "size-1.5 rounded-full",
            portfolio.state === "enabled"
              ? "bg-success"
              : portfolio.state === "paused"
                ? "bg-warning"
                : "bg-muted-foreground/50",
          )}
        />
        {t(`state.${portfolio.state}`)}
      </span>
      <span className="text-right font-mono tabular-nums text-ink">{portfolio.memberCount}</span>
      <span>
        <BudgetHealthBadge health={portfolio.budgetHealth} />
      </span>
      <span className="truncate text-muted-foreground">
        {portfolio.budget.policy ? t(`budgetPolicy.${portfolio.budget.policy}`) : "—"}
      </span>
      <span className="text-right font-mono tabular-nums text-ink">
        {portfolio.budget.amount != null
          ? formatCurrency(portfolio.budget.amount, portfolio.budget.currency ?? "USD")
          : "—"}
      </span>
      <span className="truncate font-mono tabular-nums text-muted-foreground">
        {formatFlightDates(portfolio.budget.startDate, portfolio.budget.endDate, formatDate)}
      </span>
      <MetricValueCells metrics={portfolio.metrics} currencyCode={currencyCode} />
    </Link>
  );
}
